
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { handleQueryResult, Profile } from "@/utils/supabaseHelpers";
import { getAvatarUrl } from "@/utils/storageUtils";

export function SearchUsers() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const navigate = useNavigate();

  const { data: searchResults } = useQuery({
    queryKey: ["search_users", searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return [];
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .ilike("full_name", `%${searchQuery}%`)
        .limit(5);
      
      if (error) throw error;
      return handleQueryResult<Profile>(data);
    },
    enabled: searchQuery.length > 0,
  });

  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
    setIsDropdownVisible(false);
    setSearchQuery("");
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input 
          className="pl-10 w-[300px] bg-gray-50" 
          placeholder="Search employees..." 
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsDropdownVisible(true);
          }}
          onFocus={() => setIsDropdownVisible(true)}
        />
      </div>
      
      {isDropdownVisible && searchResults && searchResults.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200">
          {searchResults.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer"
              onClick={() => handleUserClick(user.id)}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={getAvatarUrl(user.avatar_url)} alt={user.full_name || ''} />
                <AvatarFallback>
                  {user.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{user.full_name}</span>
                <span className="text-xs text-gray-500 capitalize">{user.role}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
